/**
 * SOFSEK — Ingestion Run History (§11). Tracks full/delta ingestion runs per
 * application. "Finalize" calls sfk_finalize_ingestion_run(), which — for FULL
 * runs only — deactivates any access fact / role assignment not touched since
 * the run started (never deletes; history is the product).
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { routedInsert } from '../../lib/routed_writes';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useFormatDateTime } from '../../lib/format_date';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.INGESTION_RUN.LIST';
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

interface CodeOpt { code: string; name: string; }
interface RunRow {
  run_id: number;
  application_id: number;
  run_type_code: string;
  started_at: string;
  completed_at: string | null;
  row_count: number | null;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'application', header: 'Application', defaultWidth: 200 },
  { key: 'run_type', header: 'Type', defaultWidth: 120 },
  { key: 'started_at', header: 'Started', defaultWidth: 170 },
  { key: 'completed_at', header: 'Completed', defaultWidth: 170 },
  { key: 'row_count', header: 'Rows', defaultWidth: 90 },
];

function RunInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<RunRow[]>([]);
  const [apps, setApps] = useState<CodeOpt[]>([]);
  const [types, setTypes] = useState<CodeOpt[]>([]);
  const [filter, setFilter] = useState('');
  const [appFacet, setAppFacet] = useState<string[]>([]);
  const [typeFacet, setTypeFacet] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [finalizing, setFinalizing] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [runRes, appRes, typeRes] = await Promise.all([
        apiDb.from('sfk_ingestion_run').select('run_id, application_id, run_type_code, started_at, completed_at, row_count').order('started_at', { ascending: false }),
        apiDb.from('csm_application_master').select('application_id, csm_application_master_tl ( locale_code, display_text )'),
        apiDb.from('sfk_run_type').select('run_type_code, sfk_run_type_tl ( locale_code, display_text )'),
      ]);
      if (runRes.error) throw runRes.error;
      if (appRes.error) throw appRes.error;
      if (typeRes.error) throw typeRes.error;
      setRows((runRes.data ?? []) as unknown as RunRow[]);
      setApps(((appRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        code: String(r.application_id),
        name: ((r.csm_application_master_tl ?? []) as { locale_code: string; display_text: string }[]).find(x => x.locale_code === 'EN_US')?.display_text ?? String(r.application_id),
      })));
      setTypes(((typeRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        code: r.run_type_code as string,
        name: ((r.sfk_run_type_tl ?? []) as { locale_code: string; display_text: string }[]).find(x => x.locale_code === 'EN_US')?.display_text ?? (r.run_type_code as string),
      })));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

   
  useEffect(() => { void reload(); }, []);

  const appName = (id: number) => apps.find(a => a.code === String(id))?.name ?? String(id);
  const resolveNameForType = (code: string) => types.find(x => x.code === code)?.name ?? code;

  const detail = useExpandedRows<number>();
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  // §17.B13 — related records for an ingestion run: the application it targeted and
  // its run type + row count (resolved names, §17.B4).
  const buildRelatedGroups = (r: RunRow): RelatedChildGroup[] => [
    { key: 'application', title: t('sfk.ingestion_run.related.app', 'Application'),
      items: [{ id: r.application_id, label: appName(r.application_id) }] },
    { key: 'type', title: t('sfk.ingestion_run.related.type', 'Run type'),
      items: [{ id: r.run_type_code, label: resolveNameForType(r.run_type_code), title: r.row_count != null ? `${r.row_count} rows` : undefined }] },
  ];
  const hasRelated = (_r: RunRow) => true;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (appFacet.length === 0 || appFacet.includes(String(r.application_id))) &&
      (typeFacet.length === 0 || typeFacet.includes(r.run_type_code)) &&
      (!q || appName(r.application_id).toLowerCase().includes(q))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, appFacet, typeFacet, filter, apps]);

  const finalize = async (runId: number) => {
    setFinalizing(runId); setError(null);
    try {
      const { data, error: e } = await apiDb.rpc('sfk_finalize_ingestion_run', { p_run_id: runId });
      if (e) throw e;
      const res = data as { ok?: boolean; error?: string } | null;
      if (res && res.ok === false) throw new Error(res.error ?? 'Finalize failed');
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setFinalizing(null); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.ingestion_run.title', 'Ingestion Run History')}
          description={t('sfk.ingestion_run.description', 'Full and delta access-fact ingestion runs per application.')}
          actions={<>
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
            {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> {t('sfk.ingestion_run.new', 'New Run')}</button>)}
          </>}
        />
        <StatStrip stats={[
          { value: rows.length, label: t('sfk.ingestion_run.stat.total', 'Runs') },
          { value: filtered.length, label: t('sfk.ingestion_run.stat.shown', 'Shown') },
        ]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('sfk.ingestion_run.facet.app', 'Application')} options={apps.map(a => ({ value: a.code, label: a.name }))} selected={appFacet} onChange={setAppFacet} />
            <FacetFilter label={t('sfk.ingestion_run.facet.type', 'Type')} options={types.map(x => ({ value: x.code, label: x.name }))} selected={typeFacet} onChange={setTypeFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || appFacet.length > 0 || typeFacet.length > 0} onClear={() => { setFilter(''); setAppFacet([]); setTypeFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.run_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.run_id)} hasChildren={hasRelated(r)} onToggle={() => detail.toggle(r.run_id)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'application' && appName(r.application_id)}
                        {col.key === 'run_type' && resolveNameForType(r.run_type_code)}
                        {col.key === 'started_at' && formatDateTime(r.started_at)}
                        {col.key === 'completed_at' && (r.completed_at ? formatDateTime(r.completed_at) : t('sfk.ingestion_run.in_progress', 'In progress'))}
                        {col.key === 'row_count' && (r.row_count ?? '—')}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {!r.completed_at && canChange && (
                        <button onClick={() => void finalize(r.run_id)} disabled={finalizing === r.run_id}
                          className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft disabled:opacity-60">
                          {finalizing === r.run_id ? t('sfk.ingestion_run.finalizing', 'Finalizing…') : t('sfk.ingestion_run.finalize', 'Finalize')}
                        </button>
                      )}
                    </td>
                  </tr>
                  {detail.isOpen(r.run_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r)} emptyLabel={t('common.related.none', 'No related records.')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {creating && (
        <NewRunModal session={session} apps={apps} types={types}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); void reload(); }} />
      )}
    </div>
  );
}

function NewRunModal({ session, apps, types, onClose, onSaved }: {
  session: AuthSession; apps: CodeOpt[]; types: CodeOpt[]; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [applicationId, setApplicationId] = useState(apps[0]?.code ?? '');
  const [runType, setRunType] = useState(types[0]?.code ?? 'FULL');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!applicationId) throw new Error(t('sfk.ingestion_run.err.app_required', 'Application is required.'));
      const { error: e } = await routedInsert(session, 'sfk_ingestion_run', {
        application_id: Number(applicationId),
        run_type_code: runType,
      });
      if (e) throw new Error(e.message);
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={t('sfk.ingestion_run.new', 'New Run')}
      onClose={onClose}
      width="sm"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="application_id" label={t('sfk.ingestion_run.application', 'Application')}>
        <label className={labelCls}>{t('sfk.ingestion_run.application', 'Application')}</label>
        <select value={applicationId} onChange={e => setApplicationId(e.target.value)} className={`${inputCls} bg-white`}>
          {apps.map(a => <option key={a.code} value={a.code}>{a.name}</option>)}
        </select>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="run_type_code" label={t('sfk.ingestion_run.run_type', 'Run Type')}>
        <label className={labelCls}>{t('sfk.ingestion_run.run_type', 'Run Type')}</label>
        <select value={runType} onChange={e => setRunType(e.target.value)} className={`${inputCls} bg-white`}>
          {types.map(x => <option key={x.code} value={x.code}>{x.name}</option>)}
        </select>
      </PersonalizedField>
      <p className="mb-3 text-csm-xs text-csm-watermark">
        {t('sfk.ingestion_run.help', 'Bulk-load the access facts for this run via the standard Import module targeting sfk_access_fact / sfk_app_user_role_assignment, then Finalize this run.')}
      </p>
      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function SfkIngestionRunPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <RunInner session={session} />
    </PersonalizationProvider>
  );
}
